/**
 * portalPublisher — staff side. Reads the ledgers the payroll and the stations already read and
 * publishes one snapshot per subcontractor. See snapshotBuild.ts and api/src/shared/sewerPortal.ts.
 *
 * It only ever READS SharePoint and WRITES the portal's own Postgres tables through the API.
 */
import { listQcScansCached, listReceivingScansCached, listSlipsCached, listWipsCached } from "../../lib/sp/sharedWipCache";
import { subconPayrollService } from "../../lib/sp/SubconPayrollService";
import { buildSnapshots } from "./snapshotBuild";
import { portalPublishOne } from "./portalClient";

export interface IPublishResult { sewers: number; published: number; failed: { sewer: string; error: string }[] }

export async function publishAllSnapshots(onProgress?: (done: number, total: number) => void): Promise<IPublishResult> {
  const since = new Date(Date.now() - 180 * 86_400_000).toISOString().slice(0, 10);
  const [slips, wips, receiving, qc, payouts] = await Promise.all([
    listSlipsCached(true), listWipsCached(true), listReceivingScansCached(true), listQcScansCached(true),
    subconPayrollService.listPayouts(`periodEnd ge datetime'${since}T00:00:00Z'`),
  ]);
  const built = buildSnapshots({
    slips, receiving, qc, now: new Date(),
    wips: wips.map((w) => ({ wipId: w.wipId, qty: w.qty, sku: w.sku })),
    payouts: payouts.map((p) => ({ payout: p.payout, total: p.snapshotTotal ?? 0 })),
  });
  const failed: IPublishResult["failed"] = [];
  let done = 0;
  for (const [sewer, snapshot] of built) {
    try { await portalPublishOne(sewer, snapshot); }
    catch (e) { failed.push({ sewer, error: e instanceof Error ? e.message : String(e) }); }
    onProgress?.(++done, built.size);
  }
  return { sewers: built.size, published: built.size - failed.length, failed };
}
